import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Bug, Send, ChevronLeft, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';

export default function ReportBug() {
  const navigate = useNavigate();
  const location = useLocation();
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    user_role: '',
    subject: '',
    description: '',
    page_url: location.state?.fromUrl || window.location.origin,
  });

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim() || !formData.subject.trim() || !formData.description.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSending(true);
    try {
      await axios.post(`${BACKEND_URL}/api/bug-reports/submit`, formData);
      setSubmitted(true);
      toast.success('Bug report submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit report');
    } finally {
      setSending(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-8 w-8 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Report Submitted</h2>
          <p className="text-slate-600 mb-6">Thank you for reporting this issue. Our team will review it shortly.</p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-2.5 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg font-semibold hover:from-orange-600 hover:to-amber-600 transition-all"
            data-testid="report-bug-back-btn"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-slate-600 hover:text-slate-900 mb-6 text-sm">
          <ChevronLeft className="h-4 w-4" /> Back
        </button>

        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
            <Bug className="h-5 w-5 text-red-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Report a Bug</h1>
            <p className="text-sm text-slate-500">Help us improve by reporting issues you encounter</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Your Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="John Smith"
                className="w-full"
                data-testid="report-bug-name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Your Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="john@example.com"
                className="w-full"
                data-testid="report-bug-email"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Your Role</label>
              <select
                name="user_role"
                value={formData.user_role}
                onChange={handleChange}
                className="w-full"
                data-testid="report-bug-role"
              >
                <option value="">Select your role</option>
                <option value="employer">Employer</option>
                <option value="tradesperson">Tradesperson</option>
                <option value="other">Other / Visitor</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Page URL (optional)</label>
              <input
                type="text"
                name="page_url"
                value={formData.page_url}
                onChange={handleChange}
                placeholder="https://onsitehand.co.uk/..."
                className="w-full"
                data-testid="report-bug-url"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Subject *</label>
            <input
              type="text"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              required
              placeholder="Brief description of the issue"
              className="w-full"
              data-testid="report-bug-subject"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Description *</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              rows={6}
              placeholder="Describe the bug in detail. Include steps to reproduce if possible."
              className="w-full"
              data-testid="report-bug-description"
            />
          </div>

          <button
            type="submit"
            disabled={sending}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg font-semibold hover:from-orange-600 hover:to-amber-600 transition-all disabled:opacity-50"
            data-testid="report-bug-submit-btn"
          >
            {sending ? (
              <><span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" /> Submitting...</>
            ) : (
              <><Send className="h-4 w-4" /> Submit Report</>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
